'use client';

import { useTranslations } from 'next-intl';
import type { FC, ReactElement } from 'react';
import { useMemo } from 'react';
import { VipIcon } from '@/app/components/icons/VipIcon';
import { DurationGridSelect, type SelectOption } from '@/app/components/shared/DurationGridSelect';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { FormTabType, ModelVersion, TaskSettings } from '@/app/types';
import { getAvailableOptions, isOptionAllowed } from '@/app/utils';

interface TransitionDurationSelectProps {
  isMultiFrameMode: boolean;
  currentTransitionIndex: number;
  value: number;
  onChange: (duration: number) => void;
  settings?: TaskSettings;
  updateSettings?: (newValues: Partial<TaskSettings>) => void;
  onShouldUpgrade?: (key: 'duration' | 'resolution' | 'mode' | 'amount' | 'proMode' | 'transition' | null) => void;
  modelVersion?: ModelVersion;
  type: Exclude<FormTabType, 'scene'>;
  trigger?: ReactElement;
}

export const TransitionDurationSelect: FC<TransitionDurationSelectProps> = ({
  isMultiFrameMode,
  currentTransitionIndex,
  value,
  onChange,
  settings,
  updateSettings,
  onShouldUpgrade,
  modelVersion = MODEL_VERSION.Q2,
  type,
  trigger,
}) => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const isFreeUser = userInfo?.subs_plan === 'free';

  const isFirstTransitionOrSingleFrame = !isMultiFrameMode || currentTransitionIndex === 0;

  const settingsDurationOptions = useMemo(() => {
    const availableDurations = getAvailableOptions(modelVersion, type).duration || [];
    return availableDurations.map((time) => ({
      value: time,
      label: `${time}${time === value.toString() ? 's' : ''}`,
      badge: !isOptionAllowed('duration', time, 'free', modelVersion)
        ? { content: <VipIcon className="mx-0.5 w-3" /> }
        : undefined,
      isVipOption: !isOptionAllowed('duration', time, 'free', modelVersion),
    }));
  }, [modelVersion, type, isFreeUser, value]);

  const subsequentTransitionOptions = useMemo(() => {
    return [1, 2, 3, 4, 5, 6, 7].map((time) => ({
      value: time.toString(),
      label: `${time}s`,
      badge: !isOptionAllowed('duration', time.toString(), 'free', modelVersion)
        ? { content: <VipIcon className="mx-0.5 w-3" /> }
        : undefined,
      isVipOption: !isOptionAllowed('duration', time.toString(), 'free', modelVersion),
    }));
  }, [modelVersion]);

  const options: SelectOption[] = isFirstTransitionOrSingleFrame
    ? settingsDurationOptions
    : subsequentTransitionOptions;

  const handleValueChange = (newValue: string) => {
    const duration = Number.parseInt(newValue, 10);

    if (!isOptionAllowed('duration', newValue, userInfo?.subs_plan, modelVersion)) {
      onShouldUpgrade?.('duration');
      return;
    }

    if (modelVersion === MODEL_VERSION['2.0'] && updateSettings && settings) {
      if (settings.resolution !== '720p' && duration === 8) {
        toast({
          message: t('create.createForm.settings.duration.unSupportToast'),
        });
        updateSettings({ duration, resolution: '720p' });
        onChange(duration);
        return;
      }
    }

    onChange(duration);
  };

  return (
    <DurationGridSelect
      value={value.toString()}
      options={options}
      onValueChange={handleValueChange}
      cols={Math.min(options.length, 8)}
      trigger={trigger}
    />
  );
};
